import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Alert,
  AlertIcon,
  FormControl,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  useToast,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from '@chakra-ui/react';
import fetchWithAuth from '../../utils/fetchWithAuth';

/**
 * WeightingPanel - Configure demographic weighting
 */
function WeightingPanel({ poll, pollId }) {
  const [method, setMethod] = useState('post_stratification');
  const [schemeName, setSchemeName] = useState('');
  const [trimLower, setTrimLower] = useState(0.33);
  const [trimUpper, setTrimUpper] = useState(3.0);
  const [targets, setTargets] = useState({});
  const [dimensions, setDimensions] = useState([]);
  const [currentDemographics, setCurrentDemographics] = useState({});
  const [activeWeights, setActiveWeights] = useState(null);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    fetchDimensions();
    fetchCurrentDemographics();
    fetchActiveWeights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollId]);

  const fetchDimensions = async () => {
    try {
      const response = await fetchWithAuth(`/api/polls/${pollId}/available-demographics/`);
      if (response.ok) {
        const data = await response.json();
        setDimensions(data.dimensions || []);
      }
    } catch (err) {
      console.error('Error fetching dimensions:', err);
    }
  };

  const fetchCurrentDemographics = async () => {
    try {
      const response = await fetchWithAuth(`/api/polls/${pollId}/demographics/`);
      if (response.ok) {
        const data = await response.json();
        setCurrentDemographics(data.demographics || {});

        // Initialize targets with current distribution
        const initialTargets = {};
        Object.keys(data.demographics || {}).forEach(dim => {
          initialTargets[dim] = data.demographics[dim];
        });
        setTargets(initialTargets);
      }
    } catch (err) {
      console.error('Error fetching demographics:', err);
    }
  };

  const fetchActiveWeights = async () => {
    try {
      const response = await fetchWithAuth(`/api/polls/${pollId}/active-weights/`);
      if (response.ok) {
        const data = await response.json();
        setActiveWeights(data.has_weights ? data : null);
      }
    } catch (err) {
      console.error('Error fetching active weights:', err);
    }
  };

  const handleTargetChange = (dimension, category, value) => {
    setTargets(prev => ({
      ...prev,
      [dimension]: {
        ...prev[dimension],
        [category]: parseFloat(value) / 100 // Convert percentage to decimal
      }
    }));
  };

  const handleCalculateWeights = async () => {
    // Validate that all dimensions sum to 100%
    const errors = [];
    Object.keys(targets).forEach(dim => {
      const sum = Object.values(targets[dim]).reduce((a, b) => a + b, 0);
      if (Math.abs(sum - 1.0) > 0.01) {
        errors.push(`${dim}: percentages sum to ${(sum * 100).toFixed(1)}% (should be 100%)`);
      }
    });

    if (errors.length > 0) {
      toast({
        title: 'Invalid targets',
        description: errors.join(', '),
        status: 'error',
        duration: 5000,
      });
      return;
    }

    try {
      setLoading(true);

      const response = await fetchWithAuth(`/api/polls/${pollId}/calculate-weights/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method,
          targets,
          trim_lower: trimLower,
          trim_upper: trimUpper,
          scheme_name: schemeName || `Weighting - ${new Date().toLocaleString()}`,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to calculate weights');
      }

      const data = await response.json();

      toast({
        title: 'Success!',
        description: `Calculated ${data.weights_calculated} weights using ${data.method}`,
        status: 'success',
        duration: 5000,
      });

      // Refresh active weights
      await fetchActiveWeights();
    } catch (err) {
      console.error('Error calculating weights:', err);
      toast({
        title: 'Error',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* Instructions */}
      <Alert status="info" variant="left-accent">
        <AlertIcon />
        <Box>
          <Text fontWeight="medium">Demographic Weighting</Text>
          <Text fontSize="sm" mt={1}>
            Weighting adjusts your sample to match known population demographics. This corrects
            for sampling bias (e.g., if your sample has too many young respondents, weighting
            will down-weight them to match the true age distribution).
          </Text>
        </Box>
      </Alert>

      {/* Active Weights Status */}
      {activeWeights && (
        <Box
          p={5}
          bg="green.50"
          borderRadius="md"
          borderWidth="1px"
          borderColor="green.200"
        >
          <VStack align="start" spacing={3}>
            <HStack>
              <Badge colorScheme="green" fontSize="md">Active Weighting Scheme</Badge>
            </HStack>
            <Text fontWeight="medium">{activeWeights.scheme_name}</Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} width="100%">
              <Stat>
                <StatLabel fontSize="xs">Method</StatLabel>
                <StatNumber fontSize="md">
                  {activeWeights.method === 'raking' ? 'Raking (IPF)' : 'Post-Stratification'}
                </StatNumber>
              </Stat>
              <Stat>
                <StatLabel fontSize="xs">Design Effect</StatLabel>
                <StatNumber fontSize="md">
                  {activeWeights.design_effect?.design_effect?.toFixed(3)}
                </StatNumber>
                <StatHelpText fontSize="xs">
                  n_eff = {activeWeights.design_effect?.n_effective}
                </StatHelpText>
              </Stat>
              <Stat>
                <StatLabel fontSize="xs">Weight Range</StatLabel>
                <StatNumber fontSize="md">
                  {activeWeights.statistics?.min?.toFixed(2)} - {activeWeights.statistics?.max?.toFixed(2)}
                </StatNumber>
                <StatHelpText fontSize="xs">
                  Mean: {activeWeights.statistics?.mean?.toFixed(3)}
                </StatHelpText>
              </Stat>
            </SimpleGrid>
          </VStack>
        </Box>
      )}

      {/* Configuration */}
      <Box
        p={5}
        bg="white"
        borderRadius="md"
        borderWidth="1px"
        borderColor="gray.200"
      >
        <Heading size="sm" mb={4}>Weighting Configuration</Heading>

        <VStack spacing={4} align="stretch">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl>
              <FormLabel fontSize="sm">Weighting Method</FormLabel>
              <Select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                size="sm"
              >
                <option value="post_stratification">Post-Stratification (Simple)</option>
                <option value="raking">Raking / Iterative Proportional Fitting (Advanced)</option>
              </Select>
              <Text fontSize="xs" color="gray.600" mt={1}>
                {method === 'raking'
                  ? 'Raking adjusts multiple dimensions simultaneously (recommended for complex weighting)'
                  : 'Post-stratification weights each dimension independently (simpler, faster)'}
              </Text>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Scheme Name</FormLabel>
              <Input
                value={schemeName}
                onChange={(e) => setSchemeName(e.target.value)}
                placeholder="e.g., Main weighting scheme"
                size="sm"
              />
            </FormControl>
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl>
              <FormLabel fontSize="sm">Minimum Weight (Trim Lower)</FormLabel>
              <NumberInput
                value={trimLower}
                onChange={(_, val) => setTrimLower(val)}
                min={0.1}
                max={1}
                step={0.01}
                size="sm"
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <Text fontSize="xs" color="gray.600" mt={1}>
                Prevents extreme downweighting (default: 0.33)
              </Text>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Maximum Weight (Trim Upper)</FormLabel>
              <NumberInput
                value={trimUpper}
                onChange={(_, val) => setTrimUpper(val)}
                min={1}
                max={10}
                step={0.1}
                size="sm"
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <Text fontSize="xs" color="gray.600" mt={1}>
                Prevents extreme upweighting (default: 3.0)
              </Text>
            </FormControl>
          </SimpleGrid>
        </VStack>
      </Box>

      {/* Target Percentages */}
      <Box
        p={5}
        bg="white"
        borderRadius="md"
        borderWidth="1px"
        borderColor="gray.200"
      >
        <Heading size="sm" mb={4}>Target Population Percentages</Heading>
        <Text fontSize="sm" color="gray.600" mb={4}>
          Set target percentages for each demographic group. Current sample percentages are
          shown for reference. Each dimension must sum to 100%.
        </Text>

        {dimensions.length === 0 ? (
          <Text color="gray.500">No demographic dimensions available</Text>
        ) : (
          <VStack spacing={6} align="stretch">
            {dimensions.map(dimension => {
              const currentDist = currentDemographics[dimension] || {};
              const targetDist = targets[dimension] || {};

              return (
                <Box key={dimension}>
                  <Heading size="xs" mb={3}>{dimension}</Heading>
                  <VStack spacing={2} align="stretch">
                    {Object.keys(currentDist).map(category => {
                      const currentPct = (currentDist[category] * 100).toFixed(1);
                      const targetPct = ((targetDist[category] || 0) * 100).toFixed(1);

                      return (
                        <HStack key={category} spacing={4}>
                          <Text fontSize="sm" minW="120px">{category}</Text>
                          <Text fontSize="xs" color="gray.500" minW="100px">
                            Current: {currentPct}%
                          </Text>
                          <FormControl maxW="150px">
                            <Input
                              type="number"
                              value={targetPct}
                              onChange={(e) => handleTargetChange(dimension, category, e.target.value)}
                              size="sm"
                              min="0"
                              max="100"
                              step="0.1"
                            />
                          </FormControl>
                          <Text fontSize="xs">%</Text>
                        </HStack>
                      );
                    })}
                  </VStack>
                  <Divider mt={3} />
                </Box>
              );
            })}
          </VStack>
        )}
      </Box>

      {/* Calculate Button */}
      <Button
        colorScheme="blue"
        size="lg"
        onClick={handleCalculateWeights}
        isLoading={loading}
        loadingText="Calculating Weights..."
        isDisabled={dimensions.length === 0}
      >
        Calculate and Apply Weights
      </Button>
    </VStack>
  );
}

export default WeightingPanel;
